import { useStaticQuery, graphql } from "gatsby"
import { GatsbyImage } from "gatsby-plugin-image"
import { useI18next } from "gatsby-plugin-react-i18next"
import styled from "styled-components"

import InnerWrapper from "components/InnerWrapper"
import Callout from "components/TextElements/Callout"
import ZoomImageGatsby from "components/UI-Related/ZoomImageGatsby"
import colors from "styles/colors"
import media from "styles/media"
import shadows from "styles/shadows"
import text from "styles/text"
import links from "utils/links"
import useMedia from "utils/useMedia"

export default function TheProblem() {
  const { t } = useI18next()
  const images: Queries.B2BTheProblemQuery = useStaticQuery(graphql`
    query B2BTheProblem {
      mainPic: file(relativePath: { eq: "b2b/theProblem.webp" }) {
        ...LocalImageQuery
      }
      mainPicTop: file(relativePath: { eq: "b2b/theProblemTop.webp" }) {
        ...LocalImageQuery
      }
      mainPicBottom: file(relativePath: { eq: "b2b/theProblemBottom.webp" }) {
        ...LocalImageQuery
      }
      mainPicTopM: file(relativePath: { eq: "b2b/theProblemTopM.webp" }) {
        ...LocalImageQuery
      }
      mainPicBottomM: file(relativePath: { eq: "b2b/theProblemBottomM.webp" }) {
        ...LocalImageQuery
      }
    }
  `)

  const [MainPic, MainPicTop, MainPicBottom, MainPicTopM, MainPicBottomM] = [
    images.mainPic?.childImageSharp?.gatsbyImageData,
    images.mainPicTop?.childImageSharp?.gatsbyImageData,
    images.mainPicBottom?.childImageSharp?.gatsbyImageData,
    images.mainPicTopM?.childImageSharp?.gatsbyImageData,
    images.mainPicBottomM?.childImageSharp?.gatsbyImageData,
  ]

  const imageTop = useMedia(MainPicTop, MainPicTop, MainPicTop, MainPicTopM)
  const imageBottom = useMedia(
    MainPicBottom,
    MainPicBottom,
    MainPicBottom,
    MainPicBottomM
  )

  return (
    <Wrapper>
      <InnerWrapper
        setWidth={useMedia("1440px", "86.747vw", "81.445vw", "89.333vw")}
      >
        <Content>
          <BigPicSection>
            {imageTop && (
              <ImageTop
                loading="lazy"
                image={imageTop}
                alt={t("bar graph showing some data")}
              />
            )}
            {imageBottom && (
              <ImageBottom
                image={imageBottom}
                alt={t("graph showing application data visualized")}
                loading="lazy"
              />
            )}

            <ImageContainer>
              <ZoomImageGatsby
                src={MainPic}
                alt={t("man and woman sitting on a couch in a meeting")}
                borderRadius={useMedia(
                  "11.4637px",
                  "0.691vw",
                  "0.977vw",
                  "2.667vw"
                )}
              />
            </ImageContainer>
          </BigPicSection>
          <CalloutSection>
            <Callout
              variant="lightmode"
              kicker={{
                text: t("THE PROBLEM") ?? "",
                icon: "layers2",
                color: colors.seaFoam300,
              }}
              title={{
                content: t("The future is digital. (The present is, too.)"),
                size: useMedia(text.h3, text.h3, text.h3, text.h1Mobile),
                width: useMedia("100%", "100%", "58.789vw", "100%"),
              }}
              text={{
                width: useMedia("100%", "100%", "67.578vw", "100%"),
                content: t(
                  "Every year, trillions of dollars move between businesses around the world. For too long, that’s primarily taken place through inefficient, expensive, error-prone, paper-based business models. It’s time commercial banking and payments offered the same kind of simplicity and flexibility consumers have long taken for granted with their digital payment options."
                ),
              }}
              buttons={[
                {
                  text: t("Explore Our Platform") ?? "",
                  type: "secondary",
                  arrow: true,
                  route: links.guides,
                },
              ]}
            />
          </CalloutSection>
        </Content>
      </InnerWrapper>
    </Wrapper>
  )
}

const Wrapper = styled.div`
  display: flex;
  width: 100%;
  justify-content: center;
  align-items: center;

  ${media.fullWidth} {
    padding-top: 150px;
    padding-bottom: 200px;
  }
  ${media.desktop} {
    padding-top: 9.036vw;
    padding-bottom: 12.048vw;
  }
  ${media.tablet} {
    padding-top: 2.637vw;
    padding-bottom: 12.207vw;
  }
  ${media.mobile} {
    padding-top: 21.333vw;
    padding-bottom: 66.4vw;
  }
`
const Content = styled.div`
  display: flex;
  width: 100%;
  justify-content: flex-start;
  align-items: center;
  ${media.tablet} {
    flex-direction: column-reverse;
  }
  ${media.mobile} {
    flex-direction: column-reverse;
  }
`

const BigPicSection = styled.div`
  position: relative;
  ${media.fullWidth} {
    width: 593px;
    height: 445px;
    margin-right: 202px;
  }
  ${media.desktop} {
    width: 35.723vw;
    height: 26.807vw;
    margin-right: 12.169vw;
  }
  ${media.tablet} {
    width: 57.91vw;
    height: 43.457vw;
    margin-top: 7.813vw;
  }
  ${media.mobile} {
    width: 100%;
    height: 67.04vw;
    margin-top: 10.666vw;
  }
`

const ImageTop = styled(GatsbyImage)`
  position: absolute;
  z-index: 3;
  box-shadow: ${shadows.cardMockupShadow};
  isolation: isolate;
  ${media.fullWidth} {
    width: 216.77px;
    height: 166.74px;
    left: -38.56px;
    top: -104.22px;
    border-radius: 10.4215px;
  }
  ${media.desktop} {
    width: 13.058vw;
    height: 10.045vw;
    left: -2.323vw;
    top: -6.278vw;
    border-radius: 0.628vw;
  }
  ${media.tablet} {
    width: 21.169vw;
    height: 16.283vw;
    right: -7.324vw;
    top: -10.178vw;
    border-radius: 1.018vw;
  }
  ${media.mobile} {
    width: 40.637vw;
    height: 31.2vw;
    border-radius: 1.954vw;
    bottom: -53.333vw;
    right: 4vw;
  }
`

const ImageBottom = styled(GatsbyImage)`
  position: absolute;
  z-index: 2;
  box-shadow: ${shadows.cardMockupShadow};
  isolation: isolate;
  ${media.fullWidth} {
    width: 340px;
    height: 297px;
    border-radius: 14.5902px;
    right: -112px;
    bottom: -125px;
  }
  ${media.desktop} {
    width: 20.482vw;
    height: 17.892vw;
    border-radius: 0.879vw;
    right: -6.747vw;
    bottom: -7.53vw;
  }
  ${media.tablet} {
    width: 33.203vw;
    height: 29.004vw;
    border-radius: 1.425vw;
    left: -7.422vw;
    bottom: -12.207vw;
  }
  ${media.mobile} {
    width: 63.693vw;
    height: 55.605vw;
    left: 3.867vw;
    bottom: -44.533vw;
    border-radius: 2.735vw;
  }
`

const ImageContainer = styled.div`
  width: 100%;
  height: 100%;
`

const CalloutSection = styled.div`
  ${media.fullWidth} {
    width: 550px;
  }
  ${media.desktop} {
    width: 33.133vw;
  }
  ${media.tablet} {
    width: 100%;
  }
  ${media.mobile} {
    width: 100%;
  }
`
